/**
 * @copyright Copyright 2021 Epic Systems Corporation
 * @file Summary of who marked the app as not implementing now, when, and why  (used by the Nominated Apps widget)
 * @module Epic.AppOrchard.Dashboard.NotImplementingNowInfo
 */

import { TimeIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { Box } from "@chakra-ui/react";
import { WidgetTag } from "ao/components/Dashboard/widgetHelpers";
import { INotImplementingNowDecision, IUserSecurity } from "ao/types";
import React, { FC, memo } from "react";

interface IProps {
	/** current app version ID */
	latestAppVersionId: number;
	notNowDecision: INotImplementingNowDecision;
	userSecurity: IUserSecurity;
}

/** Summary of who marked the app as not implementing now, when, and why  (used by the Nominated Apps widget) */
export const NotImplementingNowInfo: FC<IProps> = memo((props: IProps) => {
	const { notNowDecision, userSecurity, latestAppVersionId } = props;

	const isPriorVersion = latestAppVersionId !== notNowDecision.appVersionId;

	return (
		<WidgetTag
			label="Not implementing now"
			tooltipContent={
				<NotImplementingNowInfoBody
					latestAppVersionId={latestAppVersionId}
					notNowDecision={notNowDecision}
					userSecurity={userSecurity}
				/>
			}
			icon={TimeIcon}
			rightIcon={isPriorVersion ? WarningTwoIcon : undefined}
			rightIconProps={isPriorVersion ? { w: 4, h: 4, color: "yellow.500" } : undefined}
			tooltipProps={{ w: { base: "65vw", lg: "15vw" }, maxW: { base: "65vw", lg: "15vw" } }}
		></WidgetTag>
	);
});

export const NotImplementingNowInfoBody: FC<IProps> = memo((props: IProps) => {
	const { notNowDecision, userSecurity, latestAppVersionId } = props;

	const showPrivateReason =
		(userSecurity.canPurchase || userSecurity.isEpicUser) && notNowDecision.privateReason;

	const showNotNowUserName =
		(userSecurity.canPurchase || userSecurity.isEpicUser) && notNowDecision.userName;

	const isPriorVersion = latestAppVersionId !== notNowDecision.appVersionId;

	return (
		<Box>
			<Box mb="0.5em">
				{isPriorVersion && (
					<Box fontStyle="italic" mb="0.5em">
						This decision was documented for a previous app version.
					</Box>
				)}
				Marked not implementing now {showNotNowUserName ? "by " + notNowDecision.userName : ""} on{" "}
				{new Date(notNowDecision.instant).toLocaleDateString()}{" "}
			</Box>
			{showPrivateReason && (
				<>
					<Box fontWeight="bold">Private Reason: </Box>
					<Box mb="0.5em">{notNowDecision.privateReason}</Box>
				</>
			)}
			{showPrivateReason && <Box fontWeight="bold">Public Reason: </Box>}
			<Box>{notNowDecision.publicReason}</Box>
		</Box>
	);
});
